import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Container,
  Skeleton,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  TrendingUp as TakeProfitIcon,
  TrendingDown as StopLossIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import { usePrices } from '../hooks/usePrices';
import TokenIcon from '../components/TokenIcon';
import api from '../services/api';
import { formatBalance, formatCurrency } from '../utils/format';

const AllExitStrategiesPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { navigateWithLang } = useLanguageNavigation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [strategies, setStrategies] = useState([]);
  const [portfolioCoins, setPortfolioCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('percentageDiff');
  const [sortOrder, setSortOrder] = useState('desc');
  const [strategyFilter, setStrategyFilter] = useState('all'); // 'all', 'take_profit', 'stop_loss', 'no_strategy'
  const [coinFilter, setCoinFilter] = useState('all'); // 'all' or specific coin symbol
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [strategyToDelete, setStrategyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);


  // Memoize the strategies by coin grouping to prevent unnecessary recalculations
  const strategiesByCoin = useMemo(() => {
    return strategies.reduce((acc, strategy) => {
      if (!acc[strategy.symbol]) {
        acc[strategy.symbol] = {
          symbol: strategy.symbol,
          coin_name: strategy.coin_name,
          strategies: []
        };
      }
      acc[strategy.symbol].strategies.push(strategy);
      return acc;
    }, {});
  }, [strategies]);

  // Get all coins from the user's portfolio (including those without strategies)
  const allCoins = useMemo(() => {
    // Get coins with strategies
    const coinsWithStrategies = Object.values(strategiesByCoin);
    
    // Combine and deduplicate
    const allCoinsMap = new Map();
    
    // Add coins with strategies
    coinsWithStrategies.forEach(coinData => {
      allCoinsMap.set(coinData.symbol, {
        ...coinData,
        hasStrategies: true
      });
    });
    
    // Add coins without strategies from portfolio
    portfolioCoins.forEach(coinSymbol => {
      if (!allCoinsMap.has(coinSymbol)) {
        allCoinsMap.set(coinSymbol, {
          symbol: coinSymbol,
          coin_name: coinSymbol, // Use symbol as name since we only get symbols
          strategies: [],
          hasStrategies: false
        });
      }
    });
    
    return Array.from(allCoinsMap.values());
  }, [strategiesByCoin, portfolioCoins]);

  // Memoize the coin symbols array to prevent usePrices from re-running
  const coinSymbols = useMemo(() => {
    return allCoins.map(coin => coin.symbol);
  }, [allCoins]);
  
  // Fetch current prices for all coins using memoized symbols
  const { prices, loading: pricesLoading } = usePrices(coinSymbols, false, 'AllExitStrategiesPage');

  // Memoize filtered and sorted strategies for display
  const sortedStrategies = useMemo(() => {
    const allStrategies = allCoins.flatMap((coinData) => {
      if (coinData.strategies.length === 0) {
        // For coins without strategies, create a placeholder entry
        return [{
          id: `no-strategy-${coinData.symbol}`,
          coinSymbol: coinData.symbol,
          coinName: coinData.coin_name,
          strategy_type: null,
          target_price: null,
          steps: [],
          currentPrice: prices?.[coinData.symbol]?.usd || 0,
          percentageDiff: 0,
          hasStrategies: false
        }];
      }
      
      return coinData.strategies.map((strategy) => {
        const currentPrice = prices?.[coinData.symbol]?.usd || 0;
        const percentageDiff = currentPrice > 0 && strategy.target_price > 0 
          ? ((strategy.target_price - currentPrice) / currentPrice) * 100
          : 0;
        
        return {
          ...strategy,
          coinSymbol: coinData.symbol,
          coinName: coinData.coin_name,
          currentPrice,
          percentageDiff,
          hasStrategies: true
        };
      });
    });

    // Apply filters
    let filteredStrategies = allStrategies;
    
    // Filter by strategy type
    if (strategyFilter !== 'all') {
      filteredStrategies = filteredStrategies.filter(strategy => {
        if (strategyFilter === 'no_strategy') {
          return !strategy.hasStrategies;
        }
        return strategy.strategy_type === strategyFilter;
      });
    }
    
    // Filter by coin
    if (coinFilter !== 'all') {
      filteredStrategies = filteredStrategies.filter(strategy => 
        strategy.coinSymbol === coinFilter
      );
    }

    // First, separate strategies and non-strategies
    const strategiesWithCoins = filteredStrategies.filter(item => item.hasStrategies);
    const coinsWithoutStrategies = filteredStrategies.filter(item => !item.hasStrategies);
    
    // Sort strategies by the selected criteria
    const sortedStrategies = strategiesWithCoins.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'percentageDiff':
          aValue = Math.abs(a.percentageDiff);
          bValue = Math.abs(b.percentageDiff);
          break;
        case 'coinSymbol':
          aValue = a.coinSymbol;
          bValue = b.coinSymbol;
          break;
        case 'targetPrice':
          aValue = a.target_price || 0;
          bValue = b.target_price || 0;
          break;
        case 'steps':
          const aSteps = typeof a.steps === 'string' ? JSON.parse(a.steps || '[]') : a.steps || [];
          const bSteps = typeof b.steps === 'string' ? JSON.parse(b.steps || '[]') : b.steps || [];
          aValue = aSteps.length;
          bValue = bSteps.length;
          break;
        default:
          aValue = a.percentageDiff;
          bValue = b.percentageDiff;
      }

      if (typeof aValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' ? bValue - aValue : aValue - bValue;
      }
    });
    
    // Sort coins without strategies alphabetically
    const sortedCoinsWithoutStrategies = coinsWithoutStrategies.sort((a, b) => 
      a.coinSymbol.localeCompare(b.coinSymbol)
    );
    
    // Return strategies first, then coins without strategies
    return [...sortedStrategies, ...sortedCoinsWithoutStrategies];
  }, [allCoins, prices, sortBy, sortOrder, strategyFilter, coinFilter]);

  // Memoize the fetch function to prevent unnecessary re-renders
  const fetchStrategies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/exit-strategies');
      setStrategies(response.data.strategies || []);
    } catch (err) {
      console.error('Error fetching exit strategies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch portfolio coins
  const fetchPortfolioCoins = useCallback(async () => {
    try {
      const response = await api.get('/api/wallets/unique-coins');
      const coins = response.data.coins || [];
      setPortfolioCoins(coins);
    } catch (err) {
      console.error('Error fetching portfolio coins:', err);
      // Don't set error for portfolio coins as it's not critical
    }
  }, []);

  useEffect(() => {
    fetchStrategies();
    fetchPortfolioCoins();
  }, [fetchStrategies, fetchPortfolioCoins]);



  // Memoize the navigation handler
  const handleNavigateToStrategy = useCallback((coinSymbol, strategyType = null) => {
    const url = strategyType 
      ? `exit-strategy/${coinSymbol}?type=${strategyType}`
      : `exit-strategy/${coinSymbol}`;
    navigateWithLang(url);
  }, [navigateWithLang]);

  // Handle sorting
  const handleSort = useCallback((column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  }, [sortBy, sortOrder]);

  // Handle delete strategy
  const handleDeleteClick = useCallback((event, strategy) => {
    event.stopPropagation(); // Prevent row click navigation
    setStrategyToDelete(strategy);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setStrategyToDelete(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!strategyToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/api/exit-strategies/${strategyToDelete.id}`);
      setDeleteDialogOpen(false);
      setStrategyToDelete(null);
      // Refresh the strategies list
      await fetchStrategies();
    } catch (err) {
      console.error('Error deleting exit strategy:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete exit strategy');
    } finally {
      setDeleting(false);
    }
  }, [strategyToDelete, fetchStrategies]);

  // Memoize utility functions
  const getStrategyTypeIcon = useCallback((type) => {
    return type === 'take_profit' ? <TakeProfitIcon /> : <StopLossIcon />;
  }, []);

  const getStrategyTypeColor = useCallback((type) => {
    return type === 'take_profit' ? 'success' : 'error';
  }, []);

  const getStrategyTypeLabel = useCallback((type) => {
    return type === 'take_profit' ? t('exitStrategy.takeProfit') : t('exitStrategy.stopLoss');
  }, [t]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
          {t('exitStrategy.allExitStrategies')}
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Coin</TableCell>
                <TableCell>Strategy Type</TableCell>
                <TableCell>Current Price</TableCell>
                <TableCell>Target Price</TableCell>
                <TableCell>Diff %</TableCell>
                <TableCell>Steps:</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4, 5].map((item) => (
                <TableRow key={item}>
                  <TableCell><Skeleton variant="text" width={60} /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell><Skeleton variant="text" width={60} /></TableCell>
                  <TableCell><Skeleton variant="text" width={60} /></TableCell>
                  <TableCell><Skeleton variant="text" width={60} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          {t('exitStrategy.exitStrategies')}
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: 'center' }}>
        {/* Strategy Type Filter */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 200 }}>
          <FormControl size="small" fullWidth>
            <Select
              value={strategyFilter}
              onChange={(e) => setStrategyFilter(e.target.value)}
              displayEmpty
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">{t('exitStrategy.allStrategies')}</MenuItem>
              <MenuItem value="take_profit">{t('exitStrategy.takeProfit')}</MenuItem>
              <MenuItem value="stop_loss">{t('exitStrategy.stopLoss')}</MenuItem>
              <MenuItem value="no_strategy">{t('exitStrategy.noStrategy')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Coin Filter */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 200 }}>
          <FormControl size="small" fullWidth>
            <Select
              value={coinFilter}
              onChange={(e) => setCoinFilter(e.target.value)}
              displayEmpty
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">{t('exitStrategy.allCoins')}</MenuItem>
              {allCoins.map((coin) => (
                <MenuItem key={coin.symbol} value={coin.symbol}>
                  {coin.symbol}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Clear Filters Button */}
        {(strategyFilter !== 'all' || coinFilter !== 'all') && (
          <Box sx={{ display: 'flex', alignItems: 'end' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setStrategyFilter('all');
                setCoinFilter('all');
              }}
              sx={{ height: 40 }}
            >
              {t('exitStrategy.clearFilters')}
            </Button>
          </Box>
        )}
      </Box>

      {/* Filter Summary */}
      {(strategyFilter !== 'all' || coinFilter !== 'all') && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: theme.palette.grey[50], borderRadius: 1, border: `1px solid ${theme.palette.grey[200]}` }}>
          <Typography variant="body2" color="textSecondary">
            {t('exitStrategy.showingResults', { count: sortedStrategies.length })}
            {strategyFilter !== 'all' && ` • ${t('exitStrategy.strategy')}: ${strategyFilter === 'take_profit' ? t('exitStrategy.takeProfit') : strategyFilter === 'stop_loss' ? t('exitStrategy.stopLoss') : t('exitStrategy.noStrategy')}`}
            {coinFilter !== 'all' && ` • ${t('exitStrategy.coin')}: ${coinFilter}`}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

            {/* Strategies Table/Cards */}
      {allCoins.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
            {t('exitStrategy.noStrategies')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('exitStrategy.noStrategiesDescription')}
          </Typography>
        </Box>
      ) : isMobile ? (
        // Mobile: Card layout - single row per item
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Header row for all strategies */}
          <Box sx={{ 
            px: 1.5,
            py: 1,
            backgroundColor: '#e9ecef',
            borderRadius: 1,
            mb: 1
          }}>
            {/* First line: Symbol and Strategy Type */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                Symbol
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                Strategy Type
              </Typography>
            </Box>
            
            {/* Second line: Column titles */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                Steps
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                Current
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                Target
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                Diff
              </Typography>
            </Box>
            
            {/* Third line: Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                Status
              </Typography>
            </Box>
          </Box>
          
          {sortedStrategies.map((strategy) => {
            const steps = typeof strategy.steps === 'string' ? JSON.parse(strategy.steps || '[]') : strategy.steps || [];
            const triggeredSteps = steps.filter(step => step.notified_at).length;
            const totalSteps = steps.length;
            const isActive = triggeredSteps > 0 && triggeredSteps < totalSteps;
            const isCompleted = triggeredSteps === totalSteps;
            
            return (
              <Card 
                key={strategy.id}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
                onClick={() => strategy.hasStrategies ? handleNavigateToStrategy(strategy.coinSymbol, strategy.strategy_type) : handleNavigateToStrategy(strategy.coinSymbol)}
              >
                <CardContent sx={{ p: 1.5 }}>
                  {strategy.hasStrategies ? (
                    // Strategy details in two lines
                    <Box>
                      {/* First line: Coin symbol, strategy type, and status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TokenIcon coinSymbol={strategy.coinSymbol} size={24} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {strategy.coinSymbol}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            icon={getStrategyTypeIcon(strategy.strategy_type)}
                            label={getStrategyTypeLabel(strategy.strategy_type)}
                            color={getStrategyTypeColor(strategy.strategy_type)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          
                          {isCompleted ? (
                            <Chip 
                              label="✓" 
                              size="small" 
                              color="success" 
                              variant="filled"
                              sx={{ height: 18, minWidth: 18, fontSize: '0.7rem' }}
                            />
                          ) : isActive ? (
                            <Chip 
                              label="●" 
                              size="small" 
                              color="warning" 
                              variant="outlined"
                              sx={{ height: 18, minWidth: 18, fontSize: '0.7rem' }}
                            />
                          ) : (
                            <Chip 
                              label="○" 
                              size="small" 
                              color="default" 
                              variant="outlined"
                              sx={{ height: 18, minWidth: 18, fontSize: '0.7rem' }}
                            />
                          )}
                          
                          <IconButton
                            size="small"
                            onClick={(event) => handleDeleteClick(event, strategy)}
                            sx={{ 
                              color: 'error.main',
                              p: 0.5,
                              '&:hover': {
                                backgroundColor: 'error.light',
                                color: 'white'
                              }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {/* Second line: Titles only */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
                          <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>Steps</Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>Current</Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>Target</Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>Diff</Typography>
                          </Box>
                        </Box>
                      </Box>
                      
                      {/* Third line: Values */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
                          <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                              {triggeredSteps}/{totalSteps}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                              {strategy.currentPrice > 0 ? formatCurrency(strategy.currentPrice) : '...'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                              {formatCurrency(strategy.target_price)}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                              {strategy.percentageDiff > 0 ? '+' : ''}{strategy.percentageDiff.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    // Create strategy prompt in two lines
                    <Box>
                      {/* First line: Coin symbol */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TokenIcon coinSymbol={strategy.coinSymbol} size={24} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {strategy.coinSymbol}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Second line: Create button centered */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Chip
                          label={t('exitStrategy.createExitStrategy')}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.8rem',
                            py: 0.5,
                            px: 1.5,
                            backgroundColor: 'white',
                            color: theme.palette.primary.main,
                            borderColor: theme.palette.primary.main,
                            borderWidth: '2px',
                            borderRadius: '16px'
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ) : (
        // Desktop: Table layout
        <TableContainer component={Paper} sx={{ boxShadow: theme.shadows[2] }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e9ecef' }}>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.85rem',
                  padding: '12px 10px',
                  lineHeight: 1.2,
                  height: '56px',
                  whiteSpace: 'nowrap'
                }}>Coin</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.85rem',
                  padding: '12px 10px',
                  lineHeight: 1.2,
                  height: '56px',
                  whiteSpace: 'nowrap'
                }}>Strategy Type</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.85rem',
                  padding: '12px 10px',
                  lineHeight: 1.2,
                  height: '56px',
                  whiteSpace: 'nowrap'
                }}>Current Price</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.85rem',
                  padding: '12px 10px',
                  lineHeight: 1.2,
                  height: '56px',
                  whiteSpace: 'nowrap'
                }}>Target Price</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.85rem',
                  padding: '12px 10px',
                  lineHeight: 1.2,
                  height: '56px',
                  whiteSpace: 'nowrap'
                }}>Diff %</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.85rem',
                  padding: '12px 10px',
                  lineHeight: 1.2,
                  height: '56px',
                  whiteSpace: 'nowrap'
                }}>Steps:</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.85rem',
                  padding: '12px 10px',
                  lineHeight: 1.2,
                  height: '56px',
                  whiteSpace: 'nowrap'
                }}>Status</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.85rem',
                  padding: '12px 10px',
                  lineHeight: 1.2,
                  height: '56px',
                  whiteSpace: 'nowrap'
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedStrategies.map((strategy, index) => {
                const steps = typeof strategy.steps === 'string' ? JSON.parse(strategy.steps || '[]') : strategy.steps || [];
                const triggeredSteps = steps.filter(step => step.notified_at).length;
                const totalSteps = steps.length;
                const isActive = triggeredSteps > 0 && triggeredSteps < totalSteps;
                const isCompleted = triggeredSteps === totalSteps;
                
                // For coins without strategies, create a simplified row
                if (!strategy.hasStrategies) {
                  return (
                    <TableRow 
                      key={strategy.id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: theme.palette.action.hover,
                          cursor: 'pointer'
                        },
                        cursor: 'pointer',
                        backgroundColor: theme.palette.grey[25]
                      }}
                      onClick={() => handleNavigateToStrategy(strategy.coinSymbol)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TokenIcon coinSymbol={strategy.coinSymbol} size={24} />
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {strategy.coinSymbol}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {strategy.coinName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell colSpan={7}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          py: 2
                        }}>
                          <Chip
                            label={t('exitStrategy.createExitStrategy')}
                            size="large"
                            variant="outlined"
                            sx={{ 
                              fontSize: '1rem',
                              py: 1,
                              px: 2,
                              backgroundColor: 'white',
                              color: theme.palette.primary.main,
                              borderColor: theme.palette.primary.main,
                              borderWidth: '2px',
                              borderRadius: '20px',
                              '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                transform: 'scale(1.05)',
                                transition: 'all 0.2s ease-in-out'
                              }
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }
                
                // For coins with strategies, show the full table structure
                return (
                  <TableRow 
                    key={strategy.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: theme.palette.action.hover,
                        cursor: 'pointer'
                      },
                      cursor: 'pointer'
                    }}
                    onClick={() => handleNavigateToStrategy(strategy.coinSymbol, strategy.strategy_type)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TokenIcon coinSymbol={strategy.coinSymbol} size={24} />
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {strategy.coinSymbol}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {strategy.coinName}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStrategyTypeIcon(strategy.strategy_type)}
                        label={getStrategyTypeLabel(strategy.strategy_type)}
                        color={getStrategyTypeColor(strategy.strategy_type)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {strategy.currentPrice > 0 ? (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCurrency(strategy.currentPrice)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Loading...
                        </Typography>
                        )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(strategy.target_price)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: strategy.percentageDiff > 0 ? 'success.main' : 'error.main'
                        }}
                      >
                        {strategy.percentageDiff > 0 ? '+' : ''}{strategy.percentageDiff.toFixed(1)}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: triggeredSteps > 0 ? 'success.main' : 'text.secondary'
                        }}
                      >
                        {triggeredSteps}/{totalSteps}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {isCompleted ? (
                        <Chip 
                          label="Completed" 
                          size="small" 
                          color="success" 
                          variant="filled"
                        />
                      ) : isActive ? (
                        <Chip 
                          label="Active" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                        />
                      ) : (
                        <Chip 
                          label="Waiting" 
                          size="small" 
                          color="default" 
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          onClick={(event) => handleDeleteClick(event, strategy)}
                          sx={{ 
                            color: 'error.main',
                            '&:hover': {
                              backgroundColor: 'error.light',
                              color: 'white'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('exitStrategy.deleteStrategy')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('exitStrategy.deleteStrategyConfirmation', {
              coin: strategyToDelete?.coinSymbol,
              type: strategyToDelete ? getStrategyTypeLabel(strategyToDelete.strategy_type) : ''
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained" 
            disabled={deleting}
            autoFocus
          >
            {deleting ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AllExitStrategiesPage;
